import { Injectable } from '@angular/core';
import { B2bContract, B2bContractPagedResponse } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { Appointment } from '../../../services/schedules.service';

export interface B2bFleetVehicleOption {
  snapshotId: number;
  vehicleId: number | null;
  b2bContractDriverId: number | null;
  plate: string;
  model: string;
  driverName: string;
  hasDriver: boolean;
  label: string;
}

export interface B2bAppointmentMirrorFields {
  ServiceOrderId: number | null;
  VehicleId: number | null;
  Plate: string | null;
  B2BContractId: number | null;
  B2BContractDriverId: number | null;
  DriverName: string | null;
  Subject: string;
  Description: string;
}

@Injectable()
export class B2bAppointmentBookingService {
  contracts: B2bContract[] = [];
  selectedContractId: number | null = null;
  loadingContracts = false;

  fleetVehicles: B2bFleetVehicleOption[] = [];
  selectedSnapshotId: number | null = null;
  loadingFleet = false;

  hint = '';
  private customerId: number | null = null;

  constructor(private contractsService: ContractsService) {}

  get canConfirm(): boolean {
    return !!this.selectedContractId && !!this.selectedSnapshotId;
  }

  get selectedVehicle(): B2bFleetVehicleOption | null {
    return this.fleetVehicles.find(v => v.snapshotId === this.selectedSnapshotId) ?? null;
  }

  reset(): void {
    this.customerId = null;
    this.contracts = [];
    this.selectedContractId = null;
    this.fleetVehicles = [];
    this.selectedSnapshotId = null;
    this.hint = '';
    this.loadingContracts = false;
    this.loadingFleet = false;
  }

  startForCustomer(customerId: number): void {
    this.reset();
    this.customerId = customerId;
    this.loadContracts(customerId);
  }

  onContractChange(): void {
    this.fleetVehicles = [];
    this.selectedSnapshotId = null;
    this.hint = '';
    if (!this.selectedContractId) return;
    this.loadFleet(this.selectedContractId);
  }

  onVehicleChange(): void {
    this.hint = '';
  }

  validateForSubmit(): string | null {
    if (!this.selectedContractId) return 'Selecione o contrato B2B.';
    if (!this.selectedSnapshotId) return 'Selecione o veículo da frota.';
    return null;
  }

  buildMirror(customerName: string, userName: string): B2bAppointmentMirrorFields {
    const fleet = this.selectedVehicle;
    const driverName = fleet?.driverName && fleet.driverName !== 'Sem condutor'
      ? fleet.driverName
      : null;

    return {
      ServiceOrderId: null,
      VehicleId: fleet?.vehicleId ?? null,
      Plate: fleet?.plate || null,
      B2BContractId: this.selectedContractId,
      B2BContractDriverId: fleet?.b2bContractDriverId ?? null,
      DriverName: driverName,
      Subject: `${fleet?.plate || 's/ placa'} · ${fleet?.driverName || 'Sem condutor'} · ${customerName}`,
      Description: `B2B — contrato ${this.selectedContractId} · por ${userName}`
    };
  }

  applyMirror(appointment: Appointment, customerName: string, userName: string): Appointment {
    const mirror = this.buildMirror(customerName, userName);
    return { ...appointment, ...mirror };
  }

  private loadContracts(customerId: number): void {
    this.loadingContracts = true;
    this.hint = '';
    this.contractsService.listB2bContracts(1, 50, customerId).subscribe({
      next: (res: B2bContractPagedResponse) => {
        this.contracts = res?.data || [];
        this.loadingContracts = false;
        if (this.contracts.length === 0) {
          this.hint = 'Este cliente não possui contrato B2B ativo.';
          return;
        }
        if (this.contracts.length === 1) {
          this.selectedContractId = this.contracts[0].id;
          this.onContractChange();
        }
      },
      error: () => {
        this.loadingContracts = false;
        this.hint = 'Não foi possível carregar contratos B2B do cliente.';
      }
    });
  }

  private loadFleet(contractId: number): void {
    this.loadingFleet = true;
    this.contractsService.listB2bContractFrota(contractId, 1, 200).subscribe({
      next: (res: { data?: any[] }) => {
        this.fleetVehicles = (res?.data || [])
          .map((v: any) => mapFleetVehicle(v))
          .filter((v: B2bFleetVehicleOption) => v.snapshotId > 0);
        this.loadingFleet = false;
        if (this.fleetVehicles.length === 0) {
          this.hint = 'Nenhum veículo na frota deste contrato.';
        }
      },
      error: () => {
        this.loadingFleet = false;
        this.hint = 'Não foi possível carregar a frota do contrato.';
      }
    });
  }
}

/** Extrai placa do Subject gerado em buildMirror (formato: "PLACA · condutor · cliente"). */
export function extractPlateFromAppointmentSubject(subject?: string | null): string | null {
  const first = String(subject || '').split('·')[0]?.trim();
  if (!first || first.toLowerCase() === 's/ placa') {
    return null;
  }
  return first.toUpperCase();
}

export function resolveAppointmentPlate(appointment: Pick<Appointment, 'Plate' | 'Subject'>): string | null {
  const direct = String(appointment.Plate || '').trim();
  if (direct) {
    return direct.toUpperCase();
  }
  return extractPlateFromAppointmentSubject(appointment.Subject);
}

export function mapFleetVehicle(raw: any): B2bFleetVehicleOption {
  const plate = (raw.Plate || raw.plate || '').trim();
  const model = (raw.Model || raw.model || raw.Brand || raw.brand || '').trim();
  const driverName = (raw.DriverName || raw.driverName || '').trim() || 'Sem condutor';
  const driverIdRaw = raw.B2BContractDriverId ?? raw.b2BContractDriverId ?? raw.b2bContractDriverId;
  const b2bContractDriverId = driverIdRaw != null && Number(driverIdRaw) > 0 ? Number(driverIdRaw) : null;
  const snapshotId = Number(raw.Id || raw.id || 0);
  const vehicleIdRaw = raw.VehicleId ?? raw.vehicleId;
  const vehicleId = vehicleIdRaw != null && Number(vehicleIdRaw) > 0 ? Number(vehicleIdRaw) : null;

  return {
    snapshotId,
    vehicleId,
    b2bContractDriverId,
    plate,
    model,
    driverName,
    hasDriver: !!(raw.HasDriver ?? raw.hasDriver ?? b2bContractDriverId),
    label: `${plate || 's/ placa'} · ${model || '–'} · ${driverName}`
  };
}
